/**
 * Quotes Management Dataverse API
 */

import { MiddlewareAPI } from "@/lib/axios";
import { NetworkMethodEnum, Obj } from "@/types";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { mutator } from "@/utils/api/dataverse/mutator";
import {
  IActivityLogResponse,
  ICloseQuotePayload,
  ICreateOrderPayload,
  IDataverseQuote,
  IQuoteCountResponse,
  IQuoteLineItemsResponse,
  IQuoteListResponse,
  IQuoteQueryParams,
  IQuoteTotalValueResponse,
  ISubQuoteProductsResponse,
} from "../types";

export const URL = {
  quotes: "/QuoteApi",
  quoteResponse: "/QuoteResponseApi",
  quoteLineItems: "/QuoteDetailApi",
  quoteImage: "/QuoteImageApi",
  createOrder: "/CreateOrderApi",
  closeQuote: "/CloseQuote",
  activityLog: "/ActivityLogApi",
  subQuoteProduct: "/SubQuoteProductApi",
  FilterQuotes: "/FilterQuotes",
  email: "/EmailApi",
  createNote: "/CreateNoteApi",
};

export interface ICreateNotePayload {
  entityType: string;
  recordId: string;
  subject: string;
  noteText: string;
}

/**
 * Create a note attached to a quote (or other entity) record
 */
export const createNoteApi = async (
  payload: ICreateNotePayload
): Promise<any> => {
  return await mutator(NetworkMethodEnum.POST, URL.createNote, payload);
};

export interface INote {
  annotationid: string;
  subject?: string;
  notetext?: string;
  createdon?: string;
}

/**
 * Get notes attached to a quote (or other entity) record
 */
export const getNotesApi = async (
  entityType: string,
  recordId: string
): Promise<{ value: INote[] }> => {
  return await fetcher(
    `${URL.createNote}?entityType=${entityType}&recordId=${recordId}`
  );
};

/**
 * Get list of quotes with pagination from Dataverse
 */
export const getQuoteListFromDataverse = async (
  queryParams?: IQuoteQueryParams
): Promise<IQuoteListResponse> => {
  let query = "?$count=true";
if (queryParams?.nextSkipToken) {
  // Token-based pagination: pass the nextSkipToken value directly as skiptoken
  query += `&skiptoken=${queryParams.nextSkipToken}`;
} else if (queryParams?.current && queryParams.current > 1) {
  // Page-based pagination: use cookie-style skip token
  query += `&$skiptoken=<cookie pagenumber="${queryParams.current}" />`;
}

  query += `&$expand=dogma_DealersCurrency($select=dogma_name,dogma_currencysymbol,dogma_isocurrencycode,dogma_exchangerate,dogma_vatpercentage)`;

  if (queryParams?.quoteFilter) {
    query += `&quoteFilter=${queryParams.quoteFilter}`;
  }

  if (queryParams?.subProductFilter) {
    query += `&subProductFilter=${queryParams.subProductFilter}`;
  }

  return await fetcher(
    `${URL.FilterQuotes}${query}&$orderby=createdon desc`,
    queryParams?.pageSize
  );
};

/**
 * Get quote count by status code
 */
export const getQuoteCountByStatusApi = async (
  statuscode?: number
): Promise<IQuoteCountResponse> => {
  const baseURL = `${URL.quotes}?$apply=aggregate($count as count)`;
  const url =
    statuscode !== undefined
      ? `${baseURL}&$filter=statuscode eq ${statuscode}`
      : baseURL;
  return await fetcher(url);
};

/**
 * Get counts grouped by all status codes
 */
export const getAllQuoteCountsByStatusApi = async (region?: number | null): Promise<any> => {
  let query = `${URL.quotes}?$apply=`;

  if (region !== null && region !== undefined && region !== 0) {
    query += `filter(dogma_region eq ${region})/`;
  }

  query += `groupby((statuscode), aggregate($count as count))`;

  return await fetcher(query);
};

/**
 * Count quotes with dogma_followup = 1 (In Progress)
 */
export const getFollowUpChoiceCountApi = async (region?: number | null): Promise<{ value: [{ count: number }] }> => {
  const filter = region !== null && region !== undefined && region !== 0
    ? `dogma_followup eq 1 and dogma_region eq ${region}`
    : `dogma_followup eq 1`;
  const url = `${URL.quotes}?$apply=aggregate($count as count)&$filter=${filter}`;
  return await fetcher(url);
};

/**
 * Get single quote by ID from Dataverse with customer details
 */
export const getQuoteDetailFromDataverse = async (id: string): Promise<IDataverseQuote> => {
  const response = await MiddlewareAPI.get<IDataverseQuote>(
    URL.quotes +
      "?$expand=customerid_contact($select=firstname,lastname,telephone1,emailaddress1,address1_city,address1_country,address1_line1,address1_postalcode),customerid_account($select=accountid,dogma_discountfromvsbaseprice,dogma_deliverycharge,name,paymenttermscode),dogma_DealersCurrency($select=dogma_name,dogma_currencysymbol,dogma_isocurrencycode,dogma_exchangerate,dogma_vatpercentage)",
    {
      params: { id },
    },
  );
  return response.data;
};

/**
 * Create a new quote
 */
export const createQuoteApi = (body: Obj) => {
  return mutator(NetworkMethodEnum.POST, URL.quotes, body);
};

/**
 * Update quote (including status changes)
 */
export const updateQuote = async (data: {
  id: string;
  payload: Partial<IDataverseQuote>;
}): Promise<IDataverseQuote> => {
  const updatePayload = {
    id: data.id,
    ...data.payload,
  };
  return await mutator(NetworkMethodEnum.PATCH, URL.quotes, updatePayload);
};


/**
 * Create quote response
 */
export const createQuoteResponseApi = (body: Obj) => {
  return mutator(
    NetworkMethodEnum.POST,
    `${URL.quoteResponse}?quoteGuid=${body.fileName}`,
    body
  );
};

/**
 * Get quote response details
 */
export const getQuoteResponseDetailsApi = (body: Obj) => {
  return fetcher(
    `${URL.quoteResponse}?fileName=${body.fileName}.json&quoteGuid=${body.fileName}`
  );
};

/**
 * Create quote product line
 */
export const createQuoteProductLine = (body: Obj) => {
  return mutator(NetworkMethodEnum.POST, URL.quoteLineItems, body);
};

/**
 * Upload quote image
 */
export const uploadQuoteImageApi = async (
  imageData: File | Blob | ArrayBuffer,
  quoteGuid: string,
  fileName?: string,
  contentType: string = "image/png",
): Promise<any> => {
  const query = fileName
    ? `?quoteGuid=${quoteGuid}&fileName=${encodeURIComponent(fileName)}`
    : `?quoteGuid=${quoteGuid}`;
  const response = await MiddlewareAPI.post(
    `${URL.quoteImage}${query}`,
    imageData,
    {
      headers: {
        "Content-Type": contentType,
      },
    },
  );
  return response.data;
};

/**
 * Get quote images
 */
export const getQuoteImageApi = async (quoteGuid: string) => {
  const response = await MiddlewareAPI.get(
    `${URL.quoteImage}?quoteGuid=${quoteGuid}`
  );
  return response.data;
};

/**
 * Get total quotes count
 */
export const getTotalQuotesCountApi = async (region?: number | null): Promise<IQuoteCountResponse> => {
  let query = `${URL.quotes}?$apply=`;

  if (region !== null && region !== undefined && region !== 0) {
    query += `filter(dogma_region eq ${region})/`;
  }

  query += `aggregate($count as count)`;

  return await fetcher(query);
};

/**
 * Get total quotes value (sum of totalamount)
 */
export const getTotalQuotesValueApi = async (region?: number | null): Promise<IQuoteTotalValueResponse> => {
  let query = `${URL.quotes}?$apply=`;

  if (region !== null && region !== undefined && region !== 0) {
    query += `filter(dogma_region eq ${region})/`;
  }

  query += `aggregate(dogma_vsstotalamount with sum as totalValue)`;

  return await fetcher(query);
};

/**
 * Convert quote to order
 */
export const createOrderApi = async (
  payload: ICreateOrderPayload
): Promise<{ success: boolean; orderId: string }> => {
  return await mutator(NetworkMethodEnum.POST, URL.createOrder, payload);
};

/**
 * Close quote with status and reason
 */
export const closeQuoteApi = async (
  payload: ICloseQuotePayload
): Promise<any> => {
  const requestPayload = {
    QuoteClose: {
      "quoteid@odata.bind": `/quotes(${payload.quoteid})`,
      subject: payload.subject ?? "",
    },
    Status: payload.Status,
  };
  return await mutator(NetworkMethodEnum.POST, URL.closeQuote, requestPayload);
};

/**
 * Get activity logs for a specific quote
 * @param quoteId - The quote ID to filter activity logs
 * @returns Activity log data
 */
export const getActivityLogsApi = async (quoteId: string): Promise<IActivityLogResponse> => {
  const url = `${URL.activityLog}?$select=subject,_regardingobjectid_value,description,createdon&$filter=_regardingobjectid_value eq ${quoteId}`;
  return await fetcher(url);
};

/**
 * Get quote line items (quote details) for a specific quote
 * @param quoteId - The quote ID to filter line items
 * @returns Quote line items data
 */
export const getQuoteLineItemsApi = async (quoteId: string): Promise<IQuoteLineItemsResponse> => {
  const url = `${URL.quoteLineItems}?$filter=_quoteid_value eq ${quoteId}`;
  return await fetcher(url);
};

/**
 * Get sub quote products for a specific quote product/detail
 * @param quoteProductId - The quote product/detail ID to filter sub products
 * @returns Sub quote products data
 */
export const getSubQuoteProductsApi = async (quoteProductId: string): Promise<ISubQuoteProductsResponse> => {
  const url = `${URL.subQuoteProduct}?$filter=_dogma_quoteproduct_value eq ${quoteProductId}`;
  return await fetcher(url);
};

export interface IEmailAttachment {
  filename: string;
  content: string;
  mimetype: string;
}

export interface ISendQuoteEmailPayload {
  quoteIds: string[];
  subject: string;
  body: string;
  isHtml: boolean;
  attachments: IEmailAttachment[];
}

/**
 * Send quote PDF to customer via email
 */
export const sendQuotePdfEmailApi = (payload: ISendQuoteEmailPayload) => {
  return mutator(NetworkMethodEnum.POST, URL.email, payload as unknown as Obj);
};

/** Update a quote line item (pricing, quantity, window annotation, etc.) */
export const updateQuoteLineItemApi = async (id: string, body: Obj): Promise<void> => {
  await mutator(NetworkMethodEnum.PATCH, URL.quoteLineItems, { id, ...body });
};

/** Delete a quote line item by ID */
export const deleteQuoteLineItemApi = async (id: string): Promise<void> => {
  await mutator(NetworkMethodEnum.DELETE, URL.quoteLineItems, { id });
};

/** Update quote-level totals (totalamount, totaltax, totaldiscountamount, VSS fields) */
export const updateQuoteApi = async (id: string, body: Obj): Promise<void> => {
  await mutator(NetworkMethodEnum.PATCH, URL.quotes, { id, ...body });
};

/** Create sub quote products (array of new records) */
export const createSubQuoteProductsApi = async (payload: any[]): Promise<void> => {
  await mutator(NetworkMethodEnum.POST, URL.subQuoteProduct, payload);
};

/** Update existing sub quote products (changed fields only) */
export const updateSubQuoteProductsApi = async (payload: Obj[]): Promise<void> => {
  if (payload.length === 0) return;
  const cleaned = payload.map(({ 'dogma_QuoteProduct@odata.bind': _b, dogma_subquoteproductid, ...rest }: any) => ({
    id: dogma_subquoteproductid,
    ...rest,
  }));
  await mutator(NetworkMethodEnum.PATCH, URL.subQuoteProduct, cleaned);
};

/** Delete a single sub quote product by ID */
export const deleteSubQuoteProductApi = async (id: string): Promise<void> => {
  await mutator(NetworkMethodEnum.DELETE, URL.subQuoteProduct, { id });
};

/** Delete a quote image file from blob storage */
export const deleteQuoteImageFileApi = async (quoteGuid: string, fileName: string): Promise<void> => {
  await mutator(NetworkMethodEnum.DELETE, `${URL.quoteImage}?quoteGuid=${quoteGuid}&fileName=${encodeURIComponent(fileName)}`, {} as Obj);
};

/** Fetch account-specific product config (includes discountConfig) */
export const getAccountProductConfigApi = async (accountId: string): Promise<any> => {
  const response = await MiddlewareAPI.get('/AccountProductConfigApi', { params: { accountId } });
  return response.data;
};

// Re-export types for convenience
export type { ICloseQuotePayload, ICreateOrderPayload };

